import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo } from "react";

import { EmptystateMagnetColored, InfoCircle } from "components/icons/generated";
import Box from "ds/components/Box";
import CardCollapsible from "ds/components/Card/Collapsible";
import useAutoExpandOnValueChange from "ds/components/Card/Collapsible/useAutoExpandOnValueChange";
import Counter from "ds/components/Counter";
import EmptyState from "ds/components/EmptyState";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import { useToggle } from "hooks/useToggle";
import { pluralize } from "shared/Pluralize";
import { AutoattachmentTarget, AutoattachmentTargets } from "types/generated";

import NewContextAttachedProject from "../AttachedProject";
import { PROJECTS_LIMIT } from "../constants";
import { GET_AUTOATTACHMENTS_FOR_LABELS } from "./gql";

type NewContextAutoAttachedProjectsProps = {
  space?: string;
  labels: Record<"value", string>[];
  setAutoAttachedProjectCount?: (count: number) => void;
  setAutoAttachProject?: (projects: AutoattachmentTarget[], isAuto?: boolean) => void;
};

const NewContextAutoAttachedProjects = ({
  space,
  labels,
  setAutoAttachedProjectCount,
  setAutoAttachProject,
}: NewContextAutoAttachedProjectsProps) => {
  const [isCollapsed, toggle] = useToggle(true);
  const { data } = useQuery<{
    autoattachmentForLabels: AutoattachmentTargets;
  }>(GET_AUTOATTACHMENTS_FOR_LABELS, {
    variables: {
      space,
      labels: labels.map((item) => item.value),
    },
    skip: !space || !labels.length,
  });

  const limitedItems = useMemo(
    () => (data?.autoattachmentForLabels?.sample || []).slice(0, PROJECTS_LIMIT + 1),
    [data?.autoattachmentForLabels?.sample]
  );

  const count = data?.autoattachmentForLabels?.count || 0;
  const hasMore = count > PROJECTS_LIMIT;
  const remainingItemsCount = count - limitedItems.length;
  const remainingItemsLabel = pluralize("more project", remainingItemsCount);

  useAutoExpandOnValueChange(count, toggle);

  useEffect(() => {
    setAutoAttachedProjectCount?.(count);
  }, [count, setAutoAttachedProjectCount]);

  useEffect(() => {
    setAutoAttachProject?.(limitedItems, true);
  }, [limitedItems, setAutoAttachProject]);

  return (
    <CardCollapsible
      ariaLevel={2}
      ariaLabel="Auto-attached"
      title="Auto-attached"
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={<Counter count={count} />}
      contentGap="medium"
    >
      {count === 0 ? (
        <EmptyState
          padding="large 0"
          icon={EmptystateMagnetColored}
          title="Context is not attached to any items"
          caption="Use auto-attach label to attach context automatically."
        />
      ) : (
        <>
          {limitedItems.map((item) => (
            <NewContextAttachedProject key={item.id} item={item} />
          ))}
          {hasMore && (
            <Box gap="medium" justify="center" align="center" margin="small 0 0">
              <Typography variant="p-body3" tag="p" align="center">
                +{remainingItemsCount} {remainingItemsLabel}
              </Typography>
              <Tooltip widthMode="maxWidthSm" on={(props) => <Icon src={InfoCircle} {...props} />}>
                You will be able to preview the full list of projects that the context is attached
                to on Used by tab within Context view.
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </CardCollapsible>
  );
};

export default NewContextAutoAttachedProjects;
